import React from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const CONTACT_EMAIL = 'connect@transtechsys.com';

const formatValue = (value) => value || '—';

export default function SubscriptionSettings({
  subscription,
}) {
  const isActive = subscription?.active === true;
  const isTrial = subscription?.is_trialing === true;
  const hasExpiredTrial = !isActive && !isTrial && Boolean(subscription?.trial_end);
  const hasAccess = subscription?.access_allowed === true;
  const showBillingDate = isActive && Boolean(subscription?.next_billing_date_display);

  const badge = isActive
    ? {
        label: 'Active',
        className: 'bg-green-100 text-green-700',
        Icon: CheckCircleIcon,
      }
    : isTrial
      ? {
          label: 'Trial',
          className: 'bg-amber-100 text-amber-700',
          Icon: ClockIcon,
        }
      : {
          label: 'Inactive',
          className: 'bg-gray-100 text-gray-600',
          Icon: ExclamationTriangleIcon,
        };
  const BadgeIcon = badge.Icon;

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
          <p className="text-sm text-gray-500 mt-0.5">Managed internally by Ames Connect</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${badge.className}`}>
          <BadgeIcon className="h-3.5 w-3.5" />
          {badge.label}
        </span>
      </div>

      <div className="px-6 py-5 space-y-5">
        {isTrial && (
          <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 flex items-center gap-2.5">
            <ClockIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              Trial ends <span className="font-semibold">{subscription.trial_end_display}</span>
              {subscription.trial_days_remaining != null && (
                <span className="text-amber-600 ml-1">
                  · {subscription.trial_days_remaining}d left
                </span>
              )}
            </div>
          </div>
        )}

        {hasExpiredTrial && (
          <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-center gap-2.5">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="text-sm text-red-900">
              Trial ended <span className="font-semibold">{subscription.trial_end_display}</span>.
            </div>
          </div>
        )}

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            <div className="p-6 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Access</span>
                <span className="font-medium text-gray-900">
                  {hasAccess ? 'Allowed' : 'Blocked'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Plan</span>
                <span className="font-medium text-gray-900">
                  {formatValue(subscription?.plan_name)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Billing Method</span>
                <span className="font-medium text-gray-900">
                  {formatValue(subscription?.billing_method)}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Billing Interval</span>
                <span className="font-medium text-gray-900">
                  {formatValue(subscription?.billing_interval_display)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Billing Start</span>
                <span className="font-medium text-gray-900">
                  {formatValue(subscription?.billing_start_date_display)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Next Billing Date</span>
                <span className="font-medium text-gray-900">
                  {showBillingDate ? subscription.next_billing_date_display : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          To change subscription status or billing details, contact{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-red-600 hover:underline">
            {CONTACT_EMAIL}
          </a>.
        </p>
      </div>
    </div>
  );
}
